import { useEffect, useRef, useState } from "react";
import {
    useGetAllCompanyStats,
    useGetCompanyStats,
} from "../../api/admin/companyStats";
import { useDownloadComponentToPdf } from "../../hooks/useDownloadComponentToPdf";
import {
    capitalisedTermForCompany,
    pluralTermForCompany,
} from "../../copy/sharedCopy";
import { ApiCompaniesResponse } from "../../models/api/admin/company";
import { errorToast } from "../../toast";
import { ApiCompanyStatsResponse } from "../../models/api/admin/companyStats";

export const useCompanyPdfReports = (companyList?: ApiCompaniesResponse) => {
    const [showPdfReportView, setShowPdfReportView] = useState(false);
    const [allCompanyStatsData, setAllCompanyStatsData] = useState<
        ApiCompanyStatsResponse | undefined
    >();

    // When downloading a report for all clients
    const reportRef = useRef<HTMLDivElement | null>(null);
    const { generatePDF } = useDownloadComponentToPdf(reportRef);

    /**
     * The useAdminCompanies hook calls stats endpoint for each individual
     * group (enum CompanyStatsCategories)via lazy loading.
     *
     * All data is needed at once to generate report so
     * we fetch all data again at once when required.
     */
    const {
        allCompanyStatsError,
        fetchAllCompanyStats,
        isFetchingAllCompanyStats,
    } = useGetAllCompanyStats(undefined, undefined, undefined, true);

    const handleGeneratePdf = () => {
        setShowPdfReportView(true);
        generatePDF(
            () => setShowPdfReportView(false),
            `All ${pluralTermForCompany} Report`
        );
    };

    const handlePdfError = () => {
        errorToast(
            <span>
                <p>
                    Failed to fetch report data, most likely due to a time out
                    error.
                </p>
                <p>Please try downloading client reports individually.</p>
            </span>
        );
    };

    const handleDownloadPdfReport = async (): Promise<void> => {
        if (allCompanyStatsError || !allCompanyStatsData) {
            try {
                const response = await fetchAllCompanyStats();

                if (response.success) {
                    setAllCompanyStatsData(response.content);
                    handleGeneratePdf();
                } else {
                    handlePdfError();
                }
            } catch {
                handlePdfError();
            }
        } else {
            handleGeneratePdf();
        }
    };

    // When selecting an individual client from the table
    const [selectedCompanyIdForPdf, setSelectedCompanyIdForPdf] =
        useState<string>();
    const [selectedCompanyNameForPdf, setSelectedCompanyNameForPdf] =
        useState<string>();

    // stop downloading when we reset the company stats
    const [hasDownloaded, setHasDownloaded] = useState(false);
    const {
        companyStatsData: selectedCompanyStatsData,
        isCompanyStatsLoading: isSelectedCompanyStatsLoading,
    } = useGetCompanyStats(selectedCompanyIdForPdf);

    const resetSelectedCompany = () => {
        setSelectedCompanyIdForPdf(undefined);
        setSelectedCompanyNameForPdf(undefined);
        setShowPdfReportView(false);
    };

    // Generate PDF once api has returned the selected company's stats
    useEffect(() => {
        if (
            selectedCompanyStatsData &&
            selectedCompanyIdForPdf &&
            companyList &&
            !hasDownloaded
        ) {
            const selectedCompanyNameFromList = companyList.items.find(
                (company) => company.companyId === selectedCompanyIdForPdf
            )?.companyName;

            setSelectedCompanyNameForPdf(selectedCompanyNameFromList);
            setShowPdfReportView(true);
            generatePDF(
                resetSelectedCompany,
                `${
                    selectedCompanyNameFromList ?? capitalisedTermForCompany
                } Report`
            );
            setHasDownloaded(true);
        }
    }, [selectedCompanyStatsData]);

    // base loading state on preload and manual load
    const isLoadingStats = isFetchingAllCompanyStats;

    return {
        allCompanyStatsData,
        isAllCompanyStatsLoading: isLoadingStats,
        selectedCompanyStatsData,
        isSelectedCompanyStatsLoading,
        setSelectedCompanyIdForPdf,
        selectedCompanyIdForPdf,
        selectedCompanyNameForPdf,
        showPdfReportView,
        reportRef,
        handleDownloadPdfReport,
    };
};
